import { extends as _extends, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import IMask from 'imask';
import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { computeWidth } from './compute-width.esm.js';
import useExtractProps from './use-extract-props.esm.js';
import useControllableState from '../../../hooks/use-controllable.esm.js';
import createComponent from '../../../styles-system/create-component.esm.js';
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui.esm.js';
import { isNotEmptyObject } from '../../../utils/assertion.esm.js';
import { EMPTY_OBJECT, pick, omit } from '../../../utils/object.esm.js';
import { getSpace, omitSpace } from '../../../utils/props-utils.esm.js';
import { mergeRefs, getMergeRefs } from '../../../utils/react-helpers.esm.js';
import sizeSynchronize from '../../../utils/size-synchronize.esm.js';
import { BoxPrivate } from '../box/box.esm.js';
import Flex from '../flex/flex.esm.js';
import '../icon/index.esm.js';
import LoadingSpinner from '../progress/loading-spinner/loading-spinner.esm.js';
import Text from '../text/text.esm.js';
import { formikField } from '../../_adaptors/fields.esm.js';
import Icon from '../icon/icon.esm.js';

var _excluded = ["title", "inline", "__css", "maskOptions", "value", "onBlur", "onChange", "onKeyDown", "placeholder", "defaultValue"],
  _excluded2 = ["className", "maxWidth"];
// import { useWhatChanged } from '@simbathesailor/use-what-changed'
// import useWhyDidYouUpdate from '../../../hooks/use-why-did-you-update'

var Component = createComponent('input', 'csg-input');
var _InputBase = /*#__PURE__*/React.forwardRef(function InputBase(props, ref) {
  var title = props.title,
    inline = props.inline,
    __css = props.__css,
    maskOptions = props.maskOptions,
    valueProp = props.value,
    onBlur = props.onBlur,
    onChange = props.onChange,
    onKeyDown = props.onKeyDown,
    placeholder = props.placeholder,
    defaultValueProps = props.defaultValue,
    rest = _objectWithoutProperties(props, _excluded);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isMounted = _useState2[0],
    setIsMounted = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isMaskingInitialized = _useState4[0],
    setMaskingInitialized = _useState4[1];
  var mergeRefs = useMemo(function () {
    return getMergeRefs(setIsMounted);
  }, [setIsMounted]);
  // useWhyDidYouUpdate('InputBase', props)
  var maskRef = useRef();
  var localRef = useRef();
  var maskPrepareFoundADecimal = useRef(false);
  var handleChange = useCallback(function (maskEvt) {
    if (maskEvt && onChange) {
      var _maskRef$current, _maskRef$current2;
      onChange(_objectSpread2(_objectSpread2({}, maskEvt), {}, {
        unmaskedValue: maskRef === null || maskRef === void 0 ? void 0 : (_maskRef$current = maskRef.current) === null || _maskRef$current === void 0 ? void 0 : _maskRef$current.unmaskedValue,
        masked: maskRef === null || maskRef === void 0 ? void 0 : (_maskRef$current2 = maskRef.current) === null || _maskRef$current2 === void 0 ? void 0 : _maskRef$current2.masked
      }));
    }
  }, [onChange]);
  var maskOptionsWithGracefulDecimalHandler = useMemo(function () {
    if (maskOptions) {
      var allowsDecimals = (maskOptions === null || maskOptions === void 0 ? void 0 : maskOptions.scale) || false;
      if (!allowsDecimals) {
        return Object.assign({}, maskOptions, {
          prepare: function prepare(value, masked) {
            if (value === masked.radix || maskPrepareFoundADecimal.current === true) {
              maskPrepareFoundADecimal.current = true;
              return undefined;
            }
            return value;
          }
        });
      }
      return maskOptions;
    }
    return undefined;
  }, [maskOptions]);
  useEffect(function () {
    if (isMounted) {
      if (!maskRef.current) {
        if (localRef !== null && localRef !== void 0 && localRef.current && maskOptionsWithGracefulDecimalHandler) {
          maskRef.current = IMask(localRef.current, maskOptionsWithGracefulDecimalHandler);
          maskRef.current.on('accept', handleChange);
        }
        setMaskingInitialized(true);
      } else {
        if (maskOptionsWithGracefulDecimalHandler) {
          maskRef.current.updateOptions(maskOptionsWithGracefulDecimalHandler);
          maskRef.current.on('accept', handleChange);
        }
      }
    }
    return function () {
      var _maskRef$current3;
      //remove "event listener"
      maskOptionsWithGracefulDecimalHandler && (maskRef === null || maskRef === void 0 ? void 0 : (_maskRef$current3 = maskRef.current) === null || _maskRef$current3 === void 0 ? void 0 : _maskRef$current3.off('accept', handleChange));
    };
  }, [isMounted, maskOptionsWithGracefulDecimalHandler, handleChange]);

  // value must be undefined if using a mask, otherwise conflict of who has control

  // let value = undefined
  // if (syncUnmaskedValueToValue && maskOptions && maskRef && maskRef.current) {
  //   maskRef.current.unmaskedValue = `${valueProp}`
  // } else {
  //   value = valueProp
  // }
  var _useMemo = useMemo(function () {
      if (isMaskingInitialized && maskOptions && maskRef && maskRef.current) {
        var _value = valueProp || defaultValueProps;
        if (_value instanceof Date) {
          maskRef.current.typedValue = _value;
        } else {
          maskRef.current.unmaskedValue = "".concat(valueProp || defaultValueProps);
        }
      }
      return {
        value: maskOptions ? undefined : valueProp instanceof Date ? valueProp.toDateString() : valueProp,
        defaultValue: maskOptions ? undefined : defaultValueProps instanceof Date ? defaultValueProps.toDateString() : defaultValueProps
      };
    }, [isMaskingInitialized, maskOptions, valueProp, defaultValueProps]),
    value = _useMemo.value,
    defaultValue = _useMemo.defaultValue;

  // eslint-disable-next-line no-console
  // console.log(
  //   `---- input render selectedValue:  ${selectedValue} value: ${valueProp} ---`
  // )

  var onBlurHandler = useCallback(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function (e) {
    var _maskRef$current4;
    maskPrepareFoundADecimal.current = false;
    if (!maskOptions) {
      return onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    }
    return onBlur === null || onBlur === void 0 ? void 0 : onBlur(_objectSpread2(_objectSpread2({}, e), {}, {
      unmaskedValue: maskRef === null || maskRef === void 0 ? void 0 : (_maskRef$current4 = maskRef.current) === null || _maskRef$current4 === void 0 ? void 0 : _maskRef$current4.unmaskedValue
    }));
  }, [onBlur, maskOptions]);

  // Define state and function to update the value
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    showTitle = _useState6[0],
    setShowTitle = _useState6[1];
  var handleOnMouseOver = useCallback(function () {
    var inputElement = localRef.current;
    if (inputElement) {
      var isValueEmpty = !inputElement.value;
      if (isValueEmpty && placeholder) {
        inputElement.value = placeholder;
      }
      setShowTitle(inputElement.scrollWidth > inputElement.clientWidth);
      if (isValueEmpty) {
        inputElement.value = '';
      }
    }
  }, [localRef, placeholder]);
  // eslint-disable-next-line no-console
  // console.log(` ---- render input [${localRef?.current?.name}] ----\r `)

  var onKeyDownWithDecimalSkipReset = useCallback(function (e) {
    maskPrepareFoundADecimal.current = false;
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
  }, [onKeyDown]);
  var styles = useMemo(function () {
    return _objectSpread2(_objectSpread2({}, inline ? {
      display: 'inline-block'
    } : {}), __css);
  }, [__css, inline]);
  var _title = showTitle ? title || "".concat(value || placeholder) : '';

  // eslint-disable-next-line no-console
  // console.log(`---- input render value: ${value} title: ${_title}  ---`)

  return /*#__PURE__*/React.createElement(Component, _extends({
    ref: mergeRefs(ref, localRef)
  }, rest, {
    onBlur: onBlurHandler,
    onChange: !maskOptions ? onChange : undefined,
    onKeyDown: onKeyDownWithDecimalSkipReset,
    onMouseOver: handleOnMouseOver,
    placeholder: placeholder,
    value: value,
    defaultValue: defaultValue,
    title: _title,
    __themeKey: "forms",
    __css: styles,
    "data-op-ignore": true // required to ignore offers to auto fill specific fields | https://developer.1password.com/docs/web/compatible-website-design/
  }));
});
var InputBase = /*#__PURE__*/React.memo(_InputBase);
var defaultIconWrapper = {
  position: 'absolute',
  justifyContent: 'center',
  alignItems: 'center',
  top: 0,
  bottom: 0,
  gap: 'xxs'
};
var InputPrivate = /*#__PURE__*/React.forwardRef(function InputPrivate(props, _ref) {
  // useWhyDidYouUpdate('InputPrivate', props)
  var theme = useSafeThemeUI(props);
  var _useExtractProps = useExtractProps(props),
    a11yId = _useExtractProps.a11yId,
    a11yLabel = _useExtractProps.a11yLabel,
    after = _useExtractProps.after,
    afterProps = _useExtractProps.afterProps,
    autoComplete = _useExtractProps.autoComplete,
    before = _useExtractProps.before,
    beforeProps = _useExtractProps.beforeProps,
    className = _useExtractProps.className,
    Component = _useExtractProps.Component,
    defaultValue = _useExtractProps.defaultValue,
    disableBorderEffect = _useExtractProps.disableBorderEffect,
    disabled = _useExtractProps.disabled,
    height = _useExtractProps.height,
    hidableTags = _useExtractProps.hidableTags,
    inline = _useExtractProps.inline,
    isLoading = _useExtractProps.isLoading,
    isRequired = _useExtractProps.isRequired,
    maskOptions = _useExtractProps.maskOptions,
    message = _useExtractProps.message,
    messageTextProps = _useExtractProps.messageTextProps,
    mode = _useExtractProps.mode,
    onChange = _useExtractProps.onChange,
    placeholder = _useExtractProps.placeholder,
    readOnly = _useExtractProps.readOnly,
    rootProps = _useExtractProps.rootProps,
    showFocusStyle = _useExtractProps.showFocusStyle,
    size = _useExtractProps.size,
    state = _useExtractProps.state,
    sx = _useExtractProps.sx,
    tagsVariant = _useExtractProps.tagsVariant,
    type = _useExtractProps.type,
    valueProp = _useExtractProps.valueProp,
    variant = _useExtractProps.variant,
    name = _useExtractProps.name,
    color = _useExtractProps.color,
    hiderErrorIcon = _useExtractProps.hiderErrorIcon,
    autoWidthPadding = _useExtractProps.autoWidthPadding,
    rest = _useExtractProps.rest;
  var localRef = useRef();
  var ref = mergeRefs(_ref, localRef);
  var _useState7 = useState(EMPTY_OBJECT),
    _useState8 = _slicedToArray(_useState7, 2),
    autoWidthProps = _useState8[0],
    setAutoWidthProps = _useState8[1];
  var _useMemo2 = useMemo(function () {
      // this routine should be deprecated as we move away from using internal icon from theme-ui
      // to using icon from @costar/theme-ui-icon library. it's no longer necessary and size of icon
      // should be scale using combination of scale css and em unit depending on the icon.
      var _sizeSynchronize = sizeSynchronize(size !== null && size !== void 0 ? size : 'sm',
        // default to standard 1rem if not provide for default calculation
        theme, -1, -1),
        fontSize = _sizeSynchronize.fontSize,
        spinnerSize = _sizeSynchronize.spinnerSize;
      return _objectSpread2(_objectSpread2({}, size ? {
        fontSize: fontSize
      } : {}), {}, {
        spinnerSize: spinnerSize
      });
    }, [size, theme]),
    fontSize = _useMemo2.fontSize,
    spinnerSize = _useMemo2.spinnerSize;
  var _useMemo3 = useMemo(function () {
      var retVal = _objectSpread2(_objectSpread2({
        width: '100%'
      }, pick(props, ['width'])), pick(sx, ['width']));
      return retVal.width === 'auto' ? {
        autoWidth: true,
        widthProps: {}
      } : {
        widthProps: retVal
      };
    }, [props, sx]),
    autoWidth = _useMemo3.autoWidth,
    widthProps = _useMemo3.widthProps;
  var _useControllableState = _slicedToArray(useControllableState({
      value: valueProp,
      defaultValue: defaultValue
    }).state, 2),
    value = _useControllableState[0],
    setValue = _useControllableState[1];
  var rootClassName = rootProps.className,
    maxWidth = rootProps.maxWidth,
    rootRest = _objectWithoutProperties(rootProps, _excluded2);

  // useWhatChanged(
  //   [autoWidth, value, placeholder, maxWidth],
  //   'autoWidth, value, placeholder, maxWidth'
  // )

  useEffect(function () {
    if (autoWidth) {
      setAutoWidthProps(_objectSpread2({
        maxWidth: maxWidth
      }, computeWidth(localRef.current, "".concat(value || placeholder), autoWidthPadding)));
    }
  }, [autoWidth, value, placeholder, maxWidth, autoWidthPadding]);
  var handelOnChange = useCallback(function (event) {
    setValue(event.target.value);
    onChange && onChange(event);
  }, [setValue, onChange]);
  var componentProps = useMemo(function () {
    return {
      __inputProps: _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
        type: type,
        minHeight: 'calc(2em - 2px)'
      }, Component === BoxPrivate || autoWidth ? {
        width: '100%'
      } : {
        width: '0px',
        flexGrow: 1
      }), {}, {
        borderRadius: 'default',
        // explicit top and bottom to allow override below
        emPaddingTop: 'xs-3',
        emPaddingBottom: 'xs-3',
        px: 'xs'
      }, after || isLoading || state === 'danger' ? {
        pr: 'xxl'
      } : {}), before && Component === BoxPrivate ? {
        pl: 'xxl'
      } : {}), _objectSpread2(_objectSpread2({}, getSpace(rest)), pick(rest, ['lineHeight', 'minHeight']))), color ? {
        color: color
      } : {}),
      sx: omit(sx, ['width']),
      __css: _objectSpread2({
        position: 'relative',
        bg: 'white'
      }, Component === BoxPrivate ? {
        height: height
      } : {
        minHeight: height
      })
    };
  }, [type, Component, autoWidth, after, isLoading, state, before, rest, color, sx, height]);

  // eslint-disable-next-line no-console
  // console.log(`----- render input [${props?.name}] -----`)
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: classNames('csg-tui-input-wrapper', rootClassName, {
      'is-disabled': disabled,
      'is-read-only': mode !== 'select' && readOnly,
      'has-error': state === 'danger'
    }),
    __themeKey: "forms",
    variant: variant ? "input.".concat(variant) : 'input',
    disableBorderEffect: disableBorderEffect,
    fontSize: fontSize,
    disabled: disabled,
    readOnly: readOnly,
    state: state,
    inline: inline,
    hidableTags: hidableTags,
    tagsVariant: tagsVariant,
    mode: mode,
    maxWidth: autoWidth ? undefined : maxWidth
  }, _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, componentProps), before && Component !== BoxPrivate ? {
    flexWrap: 'wrap',
    pl: 'xs-2',
    pr: 'lg',
    minWidth: '0px',
    flexGrow: 1,
    alignItems: 'baseline'
  } : {}), isNotEmptyObject(autoWidthProps) ? {
    style: autoWidthProps
  } : {}), rootRest), widthProps)), before && Component === BoxPrivate ? /*#__PURE__*/React.createElement(Flex, _objectSpread2(_objectSpread2({}, defaultIconWrapper), {}, {
    left: '0.5em'
  }, beforeProps), typeof before === 'function' ? before() : before) : typeof before === 'function' ? before() : before, after && !isLoading && /*#__PURE__*/React.createElement(Flex, _objectSpread2(_objectSpread2({}, defaultIconWrapper), {}, {
    right: '0.5em'
  }, afterProps), typeof after === 'function' ? after() : after), state === 'danger' && !hiderErrorIcon ? /*#__PURE__*/React.createElement(Icon, {
    icon: "exclamation",
    size: "xxl",
    color: "danger",
    position: "absolute",
    top: "0",
    bottom: "0",
    zIndex: "2",
    right: "xs-2",
    height: "100%"
  }) : null, /*#__PURE__*/React.createElement(InputBase, _extends({
    ref: ref,
    id: a11yId,
    type: type,
    className: classNames("csg-tui-".concat(type, "-input"), className, {
      'show-focus': showFocusStyle
    }),
    "aria-invalid": state === 'danger',
    "aria-label": a11yLabel,
    "aria-required": isRequired,
    disabled: disabled,
    readOnly: readOnly,
    state: state,
    maskOptions: maskOptions,
    value: value,
    placeholder: placeholder,
    onChange: maskOptions ? onChange : handelOnChange,
    disableBorderEffect: disableBorderEffect,
    autoComplete: autoComplete,
    name: name
  }, omitSpace(omit(rest, ['width', 'lineHeight', 'fontSize', 'minHeight'])))), isLoading && /*#__PURE__*/React.createElement(LoadingSpinner, {
    display: "flex",
    size: spinnerSize,
    position: "absolute",
    top: "0",
    bottom: "0",
    zIndex: "2",
    right: "sm",
    height: "100%",
    paddingY: "xxs"
  }), message && /*#__PURE__*/React.createElement(Text, _extends({
    absolute: true,
    className: "csg-tui-".concat(type, "-input-msg"),
    fontSize: "xxs",
    lineHeight: "body",
    mt: "xs-2",
    gap: "xs-2"
  }, messageTextProps, state === 'danger' ? {
    display: 'flex',
    alignItems: 'center'
  } : undefined), message));
});
var MemoInput = /*#__PURE__*/React.memo(InputPrivate);

//@ts-ignore
var Input = /*#__PURE__*/React.forwardRef(function Input(props, ref) {
  return /*#__PURE__*/React.createElement(MemoInput, _extends({
    ref: ref
  }, props));
});
Input.Formik = formikField(Input, {
  hasFieldWrapper: true
});

export default Input;
export { InputBase, MemoInput };
//# sourceMappingURL=input.esm.js.map
